import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Trash2, Phone, Inbox } from "lucide-react";
import { API } from "@/lib/api";

const selectCls =
  "rounded-lg bg-white/5 border border-white/15 px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#D4AF37]/60";

const CONFIGS = {
  reservations: {
    title: "დაჯავშნები",
    endpoint: "reservations",
    statuses: { new: "ახალი", confirmed: "დადასტურებული", cancelled: "გაუქმებული", done: "დასრულებული" },
    styles: {
      new: "bg-green-500/15 text-green-400 border-green-500/40",
      confirmed: "bg-[#D4AF37]/15 text-[#E5C378] border-[#D4AF37]/40",
      cancelled: "bg-red-500/15 text-red-400 border-red-500/40",
      done: "bg-white/10 text-gray-300 border-white/25",
    },
    activeKey: "new",
    empty: "დაჯავშნები ჯერ არ არის",
  },
  waitlist: {
    title: "რიგი",
    endpoint: "waitlist",
    statuses: { waiting: "რიგშია", contacted: "დავუკავშირდი", freed: "გათავისუფლდა", done: "დასრულებული" },
    styles: {
      waiting: "bg-amber-500/15 text-amber-400 border-amber-500/40",
      contacted: "bg-sky-500/15 text-sky-400 border-sky-500/40",
      freed: "bg-green-500/15 text-green-400 border-green-500/40",
      done: "bg-white/10 text-gray-300 border-white/25",
    },
    activeKey: "waiting",
    empty: "რიგი ცარიელია",
  },
};

export default function AdminRequests({ kind, auth, vehicles = [], initialVehicle = "" }) {
  const cfg = CONFIGS[kind];
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [vehicleFilter, setVehicleFilter] = useState(initialVehicle);

  const load = (vf = vehicleFilter) => {
    const params = vf ? { params: { vehicle_id: vf } } : {};
    axios.get(`${API}/admin/${cfg.endpoint}`, { ...auth, ...params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(() => {
    load(initialVehicle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVehicle]);

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/${cfg.endpoint}/${id}/status`, { status }, auth);
      setItems((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("სტატუსი შეიცვალა");
    } catch {
      toast.error("შეცვლა ვერ მოხერხდა");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("ნამდვილად გსურთ ჩანაწერის წაშლა?")) return;
    try {
      await axios.delete(`${API}/admin/${cfg.endpoint}/${id}`, auth);
      setItems((prev) => prev.filter((a) => a.id !== id));
      toast.success("წაიშალა");
    } catch {
      toast.error("წაშლა ვერ მოხერხდა");
    }
  };

  if (items === null) return <p className="text-gray-500">იტვირთება...</p>;

  const activeCount = items.filter((a) => a.status === cfg.activeKey).length;

  return (
    <div data-testid={`admin-${cfg.endpoint}-section`}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold text-white">
          {cfg.title} ({items.length})
          {activeCount > 0 && <span className="ml-3 text-sm text-green-400 font-normal" data-testid={`${cfg.endpoint}-active-count`}>{activeCount} {cfg.statuses[cfg.activeKey].toLowerCase()}</span>}
        </h2>
        {vehicles.length > 0 && (
          <select
            value={vehicleFilter}
            onChange={(e) => { setVehicleFilter(e.target.value); load(e.target.value); }}
            data-testid={`${cfg.endpoint}-vehicle-filter`}
            className="rounded-xl bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/60"
          >
            <option value="" className="bg-[#141721]">ყველა ავტომობილი</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id} className="bg-[#141721]">
                {v.internal_name || v.id}
              </option>
            ))}
          </select>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/20 rounded-xl p-12 text-center text-gray-500 flex flex-col items-center gap-3" data-testid={`${cfg.endpoint}-empty`}>
          <Inbox className="w-8 h-8" />
          {cfg.empty}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((a) => {
            const open = openId === a.id;
            return (
              <div key={a.id} className="card-lux p-5" data-testid={`${cfg.endpoint}-row-${a.id}`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
                  <div className="min-w-0">
                    <p className="text-white font-semibold">{a.name}</p>
                    <a href={`tel:${a.phone}`} className="text-sm text-gray-400 flex items-center gap-1.5 mt-1 hover:text-white transition-colors">
                      <Phone className="w-3.5 h-3.5 text-[#D4AF37]" /> {a.phone}
                    </a>
                  </div>
                  <div className="text-sm text-[#E5C378] font-medium" data-testid={`${cfg.endpoint}-vehicle-${a.id}`}>{a.vehicle_internal || a.vehicle_label}</div>
                  <div className="text-xs text-gray-500">{a.vehicle_category}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(a.created_at).toLocaleString("ka-GE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={a.status}
                      onChange={(e) => setStatus(a.id, e.target.value)}
                      data-testid={`${cfg.endpoint}-status-${a.id}`}
                      className={`${selectCls} ${cfg.styles[a.status] || ""}`}
                    >
                      {Object.entries(cfg.statuses).map(([k, v]) => (
                        <option key={k} value={k} className="bg-[#141721] text-white">{v}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setOpenId(open ? null : a.id)}
                      data-testid={`${cfg.endpoint}-expand-${a.id}`}
                      className="w-8 h-8 rounded-lg bg-white/5 border border-white/15 text-gray-300 flex items-center justify-center hover:border-white/40"
                    >
                      {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => remove(a.id)}
                      data-testid={`${cfg.endpoint}-delete-${a.id}`}
                      className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center justify-center hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="mt-4 pt-4 border-t border-white/10 text-sm text-gray-300 space-y-2" data-testid={`${cfg.endpoint}-details-${a.id}`}>
                    <p><span className="text-gray-500">ავტომობილი:</span> {a.vehicle_label}</p>
                    <p><span className="text-gray-500">კატეგორია:</span> {a.vehicle_category}</p>
                    <p><span className="text-gray-500">კომენტარი:</span> {a.comment || "—"}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
